import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  required?: boolean;
  maxLength?: number;
  showCount?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, required, maxLength, showCount, value, ...props }, ref) => {
    const { t } = useTranslation();
    const currentLength = typeof value === 'string' ? value.length : 0;

    return (
      <div className="w-full">
        {label && (
          <div className="flex items-center gap-1 mb-2">
            <label className="text-sm font-medium text-text-secondary">
              {label}
            </label>
            {required && (
              <span className="text-status-error text-sm font-normal">
                (*{t('common.required')})
              </span>
            )}
          </div>
        )}
        <div className="relative">
          <textarea
            ref={ref}
            value={value}
            maxLength={maxLength}
            className={cn(
              'w-full bg-surface-light border border-glass-border rounded-2xl',
              'px-5 py-4 text-text-primary placeholder-text-tertiary',
              'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary',
              'transition-all duration-200 resize-none',
              'min-h-[140px]',
              error && 'border-status-error focus:ring-status-error/50',
              className
            )}
            {...props}
          />
        </div>
        <div className="flex justify-between items-center mt-2">
          {error && <p className="text-sm text-status-error">{error}</p>}
          {showCount && maxLength && (
            <p className={cn(
              'text-sm ml-auto',
              currentLength >= maxLength ? 'text-status-error' : 'text-text-tertiary'
            )}>
              {currentLength}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

